import { useState } from 'react'

import {
  useZoneSetPromotionalMutation,
  ZonesListDocument,
} from '~/gql/generated/graphql'

type useBulkZonePromotionalMutationProps = {
  onCompleted: (failedIds: string[]) => void
  onError: (err: unknown) => void
}

type UpdateFunctionProps = {
  zoneIds: string[]
  isPromotional: boolean
}

export function useBulkZonePromotionalMutation({
  onCompleted,
  onError,
}: useBulkZonePromotionalMutationProps): [
  (UpdateFunctionProps) => Promise<string[]>,
  boolean,
] {
  const [loading, setLoading] = useState(false)

  const [setPromotional] = useZoneSetPromotionalMutation({ onError })

  const updateFunction = async ({
    zoneIds,
    isPromotional,
  }: UpdateFunctionProps) => {
    if (zoneIds.length !== 0) {
      let failedZoneIds: string[] = []
      setLoading(true)

      zoneIds.forEach(async (id, index) => {
        const isLastZone = index + 1 === zoneIds.length

        const refetchQueries = isLastZone ? [ZonesListDocument] : undefined

        let result: Awaited<ReturnType<typeof setPromotional>>

        try {
          result = await setPromotional({
            variables: { id, is_promotional: isPromotional },
            refetchQueries,
          })
        } finally {
          // Whether it worked or not, set loading false when it gets to the last zone
          if (isLastZone) {
            setLoading(false)
            onCompleted(failedZoneIds)
          }
        }
        if (!result || result?.errors) {
          failedZoneIds = [...failedZoneIds, id]
        }
      })

      return failedZoneIds
    }
    return []
  }

  return [updateFunction, loading]
}
